import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { format, parseISO, differenceInCalendarMonths } from 'date-fns';
import {
  ArrowLeft, Loader2, ChevronRight, ChevronDown, Pencil, Save, X, Wallet, FileText,
  Layers, Package, Building2, CalendarRange, Plus, TrendingUp, Receipt,
} from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return '—'; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const todayISO = () => new Date().toISOString().slice(0, 10);
// React Router can re-render this page for a moment with no planId while a navigation settles, and
// every fetch here interpolates it straight into the URL — which is how production logged
// GET /contracts/undefined/budget and /plans/undefined/provider-budgets, both 400. Ask first.
const isPlanId = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v || ''));

const KPI = ({ label, value, hint, alert, dot }) => (
  <div className="px-4 py-3.5 bg-white" title={hint || undefined}>
    <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
      <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', dot || 'bg-slate-300')} />
      <span className={clsx(hint && 'border-b border-dotted border-slate-300')}>{label}</span>
    </div>
    <div className={clsx('text-lg font-bold tabular-nums mt-1 leading-tight', alert ? 'text-rose-600' : 'text-slate-800')}>{value}</div>
  </div>
);
// Every detail field is rendered, empty or not. The reference shows the full set because a blank
// "Referral Date" is information — it says nobody has recorded one — whereas a field that vanishes
// when empty makes the form a different shape for every contract and hides what is missing.
const D = ({ label, value }) => (
  <div className="min-w-0"><span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</span><span className="text-sm text-slate-800 break-words block mt-0.5">{value || <span className="text-slate-300">—</span>}</span></div>
);

// A stacked, collapsible section — the reference's structure. Each carries its own actions in the
// header, so the action that belongs to a table sits on that table and nowhere else.
function Section({ icon: Icon, title, count, actions, children, defaultOpen = true, sub }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 flex items-center gap-3 border-b border-slate-100">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="flex items-center gap-2 min-w-0 text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          <ChevronDown size={15} className={clsx('shrink-0 text-slate-400 transition motion-reduce:transition-none', !open && '-rotate-90')} />
          {Icon && <Icon size={15} className="text-primary-600 shrink-0" />}
          <span className="text-sm font-bold text-slate-700 truncate">{title}</span>
          {count != null && <span className="text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">{count}</span>}
          {sub && <span className="text-[11px] text-slate-400 truncate hidden sm:inline">{sub}</span>}
        </button>
        <span className="flex-1" />
        {open && actions}
      </div>
      {open && <div className="p-5">{children}</div>}
    </section>
  );
}

const TH = ({ children, align, title }) => (
  <th title={title} className={clsx('px-2.5 py-2 font-semibold whitespace-nowrap', align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left')}>{children}</th>
);

// One budget cell that saves on blur — used for both category and service rows.
function BudgetInput({ value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <input type="number" step="0.01" value={v} onChange={(e) => setV(e.target.value)}
      onBlur={() => { if ((Number(v) || 0) !== (Number(value) || 0)) onSave(v); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className="input-field !text-xs !py-1 !w-24 text-right" />
  );
}

// ─── 3. Plan Budget ───────────────────────────────────────────────────────────
// Category budgets, with the reference's full column set. Excluded / Unallocated / Available were
// already computed by the API and simply were not being shown.
function PlanBudgetSection({ planId, data, reload }) {
  const [q, setQ] = useState('');
  const [pick, setPick] = useState(null);   // { type:'category'|'service', ... }
  const [catRes, setCatRes] = useState([]);
  const [svcRes, setSvcRes] = useState([]);
  const [amt, setAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (pick) { setCatRes([]); setSvcRes([]); return undefined; }
    const s = q.trim().toLowerCase();
    const cats = data?.all_categories || [];
    const used = new Set((data?.rows || []).map((r) => r.key));
    setCatRes(s ? cats.filter((c) => !used.has(c.key) && c.label.toLowerCase().includes(s)).slice(0, 6) : []);
    if (s.length < 2) { setSvcRes([]); return undefined; }
    const t = setTimeout(() => {
      api.get(`/plan-management/contracts/${planId}/services`, { params: { search: q.trim() } })
        .then((r) => setSvcRes((r.data?.items || []).slice(0, 6))).catch(() => setSvcRes([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, pick, planId, data]);

  // Row flags travel with every save. The API treats an absent flag as false, so sending only the
  // amount would silently un-exclude a category the moment somebody corrected its budget.
  const saveCat = async (row, val) => {
    try {
      await api.put(`/plan-management/contracts/${planId}/budget`, { budgets: [{ support_category: row.key, fund_amount: Number(val) || 0, exclude_from_allocation: !!row.exclude_from_allocation, stated_support: !!row.stated_support }] });
      reload();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save that'); }
  };
  const toggleExcluded = async (row) => {
    try {
      await api.put(`/plan-management/contracts/${planId}/budget`, { budgets: [{ support_category: row.key, fund_amount: Number(row.budget) || 0, exclude_from_allocation: !row.exclude_from_allocation, stated_support: !!row.stated_support }] });
      reload();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save that'); }
  };
  const delCat = async (row) => {
    if (!window.confirm(`Remove the ${row.label} budget?`)) return;
    try { await api.put(`/plan-management/contracts/${planId}/budget`, { budgets: [{ support_category: row.key, fund_amount: 0, exclude_from_allocation: false, stated_support: false }] }); toast.success('Removed'); reload(); } catch (e) { toast.error(e.response?.data?.message || 'That did not work'); }
  };
  const add = async () => {
    if (!pick) return toast.error('Pick a category or service');
    const a = Number(amt) || 0;
    setBusy(true);
    try {
      if (pick.type === 'category') await api.put(`/plan-management/contracts/${planId}/budget`, { budgets: [{ support_category: pick.key, fund_amount: a }] });
      else await api.post(`/funding/plans/${planId}/lines`, { serviceTypeName: pick.name, supportItemNumber: pick.code, budget: a });
      toast.success('Added'); setPick(null); setQ(''); setAmt(''); setCatRes([]); setSvcRes([]); setAdding(false); reload();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };

  const rows = data?.rows || [];
  const buckets = ['core', 'capacity', 'capital'];
  const byBucket = {}; rows.forEach((r) => { (byBucket[r.bucket] = byBucket[r.bucket] || []).push(r); });

  return (
    <Section
      icon={Wallet} title="Plan Budget" count={rows.length}
      actions={(
        <div className="flex items-center gap-2">
          <button onClick={() => setAdding((v) => !v)} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Plus size={13} /> Create</button>
        </div>
      )}
    >
      {adding && (
        <div className="flex items-center gap-2 flex-wrap mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
          <div className="relative flex-1 min-w-[260px]">
            <input value={pick ? pick.label : q} onChange={(e) => { setPick(null); setQ(e.target.value); }} placeholder="Search a category or service to add…" className="input-field !text-sm !py-2 w-full" autoFocus />
            {!pick && (catRes.length > 0 || svcRes.length > 0) && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                {catRes.length > 0 && <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-400 bg-slate-50">Categories</div>}
                {catRes.map((c) => (
                  <button key={c.key} onClick={() => setPick({ type: 'category', key: c.key, label: c.label })} className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">{c.label}</button>
                ))}
                {svcRes.length > 0 && <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-400 bg-slate-50">Services (stated supports)</div>}
                {svcRes.map((it) => (
                  <button key={it.code} onClick={() => setPick({ type: 'service', code: it.code, name: it.name, label: it.name, cap: it.cap, category_label: it.category_label })} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50">
                    <span className="text-slate-700">{it.name}</span> <span className="text-slate-400 font-mono">{it.code}</span>{it.cap != null && <span className="text-slate-400"> · cap {money(it.cap)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Budget $" className="input-field !text-sm !py-2 !w-32 text-right" />
          <button onClick={add} disabled={busy || !pick} className="btn-primary !text-sm !px-4 !py-2 disabled:opacity-40">{busy ? '…' : 'Add'}</button>
          <button onClick={() => { setAdding(false); setPick(null); setQ(''); setAmt(''); }} className="btn-secondary !text-sm !px-3 !py-2">Cancel</button>
          {pick?.type === 'service' && <span className="text-[11px] text-slate-400 w-full">{pick.category_label} · NDIS cap {pick.cap != null ? money(pick.cap) : '—'} — added under Stated Supports below.</span>}
        </div>
      )}

      {rows.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No category budgets yet — use Create above.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200">
              <tr>
                <TH>Action</TH>
                <TH>Category</TH>
                <TH align="center" title="Excluded categories do not consume the plan's unallocated pool">Excluded</TH>
                <TH align="right">Budget</TH>
                <TH align="right" title="Budget not yet assigned to a stated support">Unallocated</TH>
                <TH align="right" title="Value of invoices committed against this category">Delivered</TH>
                <TH align="right" title="Budget minus delivered">Remaining</TH>
                <TH align="right" title="What can still be spent freely: unallocated budget minus delivered">Available</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {buckets.map((bk) => {
                const cr = byBucket[bk] || [];
                if (!cr.length) return null;
                const t = cr.reduce((a, r) => ({
                  b: a.b + (Number(r.budget) || 0), u: a.u + (Number(r.unallocated) || 0),
                  d: a.d + (Number(r.delivered) || 0), r: a.r + (Number(r.remaining) || 0), v: a.v + (Number(r.available) || 0),
                }), { b: 0, u: 0, d: 0, r: 0, v: 0 });
                return (
                  <React.Fragment key={bk}>
                    <tr className="bg-slate-50/70">
                      <td />
                      <td className="px-2.5 py-1.5 font-bold text-slate-700">{data.buckets[bk]}</td>
                      <td />
                      <td className="px-2.5 py-1.5 text-right font-bold tabular-nums">{money(t.b)}</td>
                      <td className="px-2.5 py-1.5 text-right font-bold tabular-nums">{money(t.u)}</td>
                      <td className="px-2.5 py-1.5 text-right font-bold tabular-nums">{money(t.d)}</td>
                      <td className="px-2.5 py-1.5 text-right font-bold tabular-nums">{money(t.r)}</td>
                      <td className="px-2.5 py-1.5 text-right font-bold tabular-nums">{money(t.v)}</td>
                    </tr>
                    {cr.map((r) => (
                      <tr key={r.key} className="hover:bg-slate-50">
                        <td className="px-2.5 py-2"><button onClick={() => delCat(r)} title="Remove this category budget" className="text-slate-300 hover:text-rose-600"><X size={13} /></button></td>
                        <td className="px-2.5 py-2 text-slate-700">
                          {r.label}
                          {r.stated_support && <span className="ml-1.5 text-[9.5px] font-semibold px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 align-middle">Stated</span>}
                        </td>
                        <td className="px-2.5 py-2 text-center">
                          <input type="checkbox" checked={!!r.exclude_from_allocation} onChange={() => toggleExcluded(r)}
                            aria-label={`Exclude ${r.label} from allocation`} className="align-middle focus-visible:ring-2 focus-visible:ring-primary-400" />
                        </td>
                        <td className="px-2.5 py-2 text-right"><BudgetInput value={r.budget} onSave={(v) => saveCat(r, v)} /></td>
                        <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{money(r.unallocated)}</td>
                        <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{money(r.delivered)}</td>
                        <td className={clsx('px-2.5 py-2 text-right tabular-nums font-semibold', r.remaining < 0 ? 'text-rose-600' : 'text-slate-700')}>{money(r.remaining)}</td>
                        <td className={clsx('px-2.5 py-2 text-right tabular-nums font-semibold', r.available < 0 ? 'text-rose-600' : 'text-slate-700')}>{money(r.available)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ─── 4. Funding Component ─────────────────────────────────────────────────────
// PACE releases a plan's money in periods rather than all at once, so "budget" and "what can be
// spent today" are two different numbers. This section is the second one.
function FundingComponentSection({ planId }) {
  const [rows, setRows] = useState(undefined);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => {
    if (!isPlanId(planId)) { setRows([]); return; }
    api.get(`/plan-management/plans/${planId}/funding-periods`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const today = todayISO();
  const isActive = (p) => String(p.start_date || '').slice(0, 10) <= today && today <= String(p.end_date || '').slice(0, 10);
  const list = rows || [];
  const active = list.filter(isActive);
  const shown = showAll || active.length === 0 ? list : active;
  const months = (p) => {
    try { return Math.max(1, differenceInCalendarMonths(parseISO(String(p.end_date).slice(0, 10)), parseISO(String(p.start_date).slice(0, 10))) + 1); } catch { return null; }
  };

  return (
    <Section
      icon={CalendarRange} title="Funding Component" count={list.length}
      sub={list.length ? `Funding Periods (${list.length})` : undefined}
      actions={list.length > active.length && active.length > 0 ? (
        <button onClick={() => setShowAll((v) => !v)} className="btn-secondary !text-[11px] !px-2.5 !py-1.5">{showAll ? 'Show active only' : 'View All'}</button>
      ) : null}
    >
      {rows === undefined ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>
        : list.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No funding periods on this plan — the whole budget is treated as available from the start date.
            PACE plans release money in periods; import or generate them from the participant&apos;s plan to track releases.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200">
                <tr>
                  <TH>Action</TH>
                  <TH>Funding Component</TH>
                  <TH>Dates</TH>
                  <TH align="right" title="Amount released for this period">Budget</TH>
                  <TH align="right" title="Committed by invoices dated inside this period">Delivered</TH>
                  <TH align="right" title="Released minus delivered">Available</TH>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {shown.map((p) => {
                  const m = months(p);
                  return (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-2.5 py-2">
                        <span className={clsx('text-[9.5px] font-semibold px-1.5 py-0.5 rounded', p.is_open === false ? 'bg-slate-100 text-slate-400' : 'bg-emerald-50 text-emerald-700')}>{p.is_open === false ? 'Closed' : 'Open'}</span>
                      </td>
                      <td className="px-2.5 py-2 text-slate-700">
                        Funding Period {p.period_no}{m ? ` (${m} month${m === 1 ? '' : 's'})` : ''}
                        {isActive(p) && <span className="ml-1.5 text-[9.5px] font-semibold px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 align-middle">Active</span>}
                        {p.support_category && <span className="block text-[10.5px] text-slate-400">{cap(p.support_category)}</span>}
                      </td>
                      <td className="px-2.5 py-2 text-slate-500 whitespace-nowrap tabular-nums">{fmtD(p.start_date)} – {fmtD(p.end_date)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums">{money(p.released_amount)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{money(p.committed)}</td>
                      <td className={clsx('px-2.5 py-2 text-right tabular-nums font-semibold', Number(p.remaining) < 0 ? 'text-rose-600' : 'text-slate-700')}>{money(p.remaining)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!showAll && active.length > 0 && list.length > active.length && (
              <p className="text-[11px] text-slate-400 mt-2">Showing the {active.length} active period{active.length === 1 ? '' : 's'} of {list.length}. Use View All to see the whole schedule.</p>
            )}
          </div>
        )}
    </Section>
  );
}

// ─── 5. Stated Supports ───────────────────────────────────────────────────────
// Budget ring-fenced to one price-book item. Its own section on the reference, because a stated
// support is a different instrument from a category budget: it can only be spent on that item.
function StatedSupportsSection({ planId, data, reload }) {
  const lines = data?.service_lines || [];
  const save = async (id, val) => { try { await api.put(`/funding/plans/${planId}/lines/${id}`, { budget: Number(val) || 0 }); reload(); } catch (e) { toast.error(e.response?.data?.message || 'Could not save that'); } };
  const del = async (id) => { if (!window.confirm('Remove this stated support?')) return; try { await api.delete(`/funding/plans/${planId}/lines/${id}`); toast.success('Removed'); reload(); } catch (e) { toast.error(e.response?.data?.message || 'That did not work'); } };
  return (
    <Section icon={Package} title="Stated Supports" count={lines.length}>
      {lines.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No stated supports — add a service from Plan Budget · Create to ring-fence budget for a specific price-book item.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200">
              <tr>
                <TH>Action</TH>
                <TH>Category</TH>
                <TH>Support Item</TH>
                <TH>Support Item Number</TH>
                <TH align="right">Total Funding</TH>
                <TH align="right">Delivered</TH>
                <TH align="right">Remaining</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {lines.map((s) => (
                <tr key={s.line_id} className="hover:bg-slate-50">
                  <td className="px-2.5 py-2"><button onClick={() => del(s.line_id)} title="Remove" className="text-slate-300 hover:text-rose-600"><X size={13} /></button></td>
                  <td className="px-2.5 py-2 text-slate-500">{s.category_label}</td>
                  <td className="px-2.5 py-2 text-slate-700"><span className="block max-w-[260px] truncate" title={s.name}>{s.name}</span></td>
                  <td className="px-2.5 py-2 font-mono text-[10.5px] text-slate-500 whitespace-nowrap">{s.item || '—'}{s.cap != null && <span className="text-slate-300"> · cap {money(s.cap)}</span>}</td>
                  <td className="px-2.5 py-2 text-right"><BudgetInput value={s.budget} onSave={(v) => save(s.line_id, v)} /></td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{money(s.delivered)}</td>
                  <td className={clsx('px-2.5 py-2 text-right tabular-nums font-semibold', s.remaining < 0 ? 'text-rose-600' : 'text-slate-700')}>{money(s.remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ─── 6. Service Provider Category Budgets ─────────────────────────────────────
// Caps one provider's spend inside one category. The reference also carries agreement dates and a
// "Hide expired Agreements" toggle; we hold no agreement term against these caps, so neither is
// shown — a toggle that can never hide anything is worse than its absence.
function ProviderBudgetsSection({ planId }) {
  const [data, setData] = useState(undefined);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ provider_id: '', support_category: '', cap_amount: '' });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!isPlanId(planId)) { setData({ budgets: [], providers: [], categories: [] }); return; }
    api.get(`/plan-management/plans/${planId}/provider-budgets`)
      .then((r) => setData(r.data || { budgets: [], providers: [], categories: [] }))
      .catch(() => setData({ budgets: [], providers: [], categories: [] }));
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.provider_id || !form.support_category) return toast.error('Pick a provider and a category');
    setBusy(true);
    try {
      await api.put(`/plan-management/plans/${planId}/provider-budgets`, { provider_id: form.provider_id, support_category: form.support_category, cap_amount: Number(form.cap_amount) || 0 });
      toast.success('Saved'); setForm({ provider_id: '', support_category: '', cap_amount: '' }); setAdding(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove the ${row.provider_name} cap on ${cap(row.support_category)}?`)) return;
    try { await api.put(`/plan-management/plans/${planId}/provider-budgets`, { delete: row.id }); toast.success('Removed'); load(); } catch (e) { toast.error(e.response?.data?.message || 'That did not work'); }
  };
  const saveCap = async (row, val) => {
    try { await api.put(`/plan-management/plans/${planId}/provider-budgets`, { provider_id: row.provider_id, support_category: row.support_category, cap_amount: Number(val) || 0 }); load(); } catch (e) { toast.error(e.response?.data?.message || 'Could not save that'); }
  };

  const budgets = data?.budgets || [];
  const CATS = data?.categories || [];
  const catLabel = (k) => (CATS.find((c) => c.key === k) || {}).label || cap(k);

  return (
    <Section
      icon={Building2} title="Service Provider Category Budgets" count={budgets.length}
      actions={<button onClick={() => setAdding((v) => !v)} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Plus size={13} /> Create</button>}
    >
      {adding && (
        <div className="flex items-end gap-2 flex-wrap mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
          <div><label className="block text-[11px] text-slate-500 mb-1">Provider</label>
            <select value={form.provider_id} onChange={(e) => setForm((p) => ({ ...p, provider_id: e.target.value }))} className="input-field !text-xs !py-1.5 !w-56">
              <option value="">Select…</option>
              {(data?.providers || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className="block text-[11px] text-slate-500 mb-1">Category</label>
            <select value={form.support_category} onChange={(e) => setForm((p) => ({ ...p, support_category: e.target.value }))} className="input-field !text-xs !py-1.5 !w-56">
              <option value="">Select…</option>
              {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div><label className="block text-[11px] text-slate-500 mb-1">Cap</label>
            <input type="number" step="0.01" value={form.cap_amount} onChange={(e) => setForm((p) => ({ ...p, cap_amount: e.target.value }))} className="input-field !text-xs !py-1.5 !w-28 text-right" />
          </div>
          <button onClick={add} disabled={busy} className="btn-primary !text-xs !px-3 !py-2 disabled:opacity-40">{busy ? '…' : 'Add'}</button>
          <button onClick={() => setAdding(false)} className="btn-secondary !text-xs !px-3 !py-2">Cancel</button>
        </div>
      )}

      {data === undefined ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>
        : budgets.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No provider caps on this contract — every assigned provider can bill against the category budgets above.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200">
                <tr>
                  <TH>Action</TH>
                  <TH>Service Provider</TH>
                  <TH>Category</TH>
                  <TH align="center">Status</TH>
                  <TH align="right" title="The most this provider may bill in this category">Budget</TH>
                  <TH align="right" title="Committed by this provider's invoices in this category">Delivered</TH>
                  <TH align="right">Remaining</TH>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {budgets.map((b) => {
                  const capAmt = Number(b.cap_amount) || 0; const used = Number(b.committed) || 0;
                  const left = capAmt - used;
                  return (
                    <tr key={b.id} className="hover:bg-slate-50">
                      <td className="px-2.5 py-2"><button onClick={() => remove(b)} title="Remove this cap" className="text-slate-300 hover:text-rose-600"><X size={13} /></button></td>
                      <td className="px-2.5 py-2 text-slate-700"><span className="block max-w-[220px] truncate">{b.provider_name || '—'}</span></td>
                      <td className="px-2.5 py-2 text-slate-500">{catLabel(b.support_category)}</td>
                      <td className="px-2.5 py-2 text-center">
                        <span className={clsx('text-[9.5px] font-semibold px-1.5 py-0.5 rounded', left < 0 ? 'bg-rose-50 text-rose-700' : left === 0 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700')}>
                          {left < 0 ? 'Over cap' : left === 0 ? 'Fully used' : 'Within cap'}
                        </span>
                      </td>
                      <td className="px-2.5 py-2 text-right"><BudgetInput value={b.cap_amount} onSave={(v) => saveCap(b, v)} /></td>
                      <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{money(used)}</td>
                      <td className={clsx('px-2.5 py-2 text-right tabular-nums font-semibold', left < 0 ? 'text-rose-600' : 'text-slate-700')}>{money(left)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </Section>
  );
}

export default function ServiceContract() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  // Loaded once here and shared: Plan Budget and Stated Supports are two views of the same payload,
  // and fetching it twice would let them disagree after an edit.
  const [budget, setBudget] = useState(null);

  const load = useCallback(() => {
    if (!isPlanId(planId)) { setData(null); return; }
    setData(undefined);
    api.get(`/plan-management/contracts/${planId}`).then((r) => { setData(r.data); setForm(r.data?.contract || {}); }).catch(() => setData(null));
  }, [planId]);
  const loadBudget = useCallback(() => {
    if (!isPlanId(planId)) { setBudget(false); return; }
    api.get(`/plan-management/contracts/${planId}/budget`).then((r) => setBudget(r.data)).catch(() => setBudget(false));
  }, [planId]);
  useEffect(() => { load(); loadBudget(); }, [load, loadBudget]);
  const reloadAll = useCallback(() => { loadBudget(); load(); }, [loadBudget, load]);

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/plan-management/contracts/${planId}`, {
        budget: form.budget, contract_status: form.contract_status, contract_ref: form.contract_ref, contract_number: form.contract_number,
        funding_source: form.funding_source, funding_management: form.funding_management,
        start_date: form.start_date, end_date: form.end_date, ndis_plan_start: form.ndis_plan_start, ndis_plan_end: form.ndis_plan_end,
        referral_date: form.referral_date, progress_report_date: form.progress_report_date, review_submit_date: form.review_submit_date,
        setup_completed_by: form.setup_completed_by, assigned_to: form.assigned_to, pace_participant: !!form.pace_participant,
      });
      toast.success('Contract saved'); setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); } finally { setBusy(false); }
  };

  if (data === undefined) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;
  if (!data) return (
    <div className="p-6 max-w-3xl mx-auto">
      <button onClick={() => navigate('/plan-management')} className="text-sm text-primary-600 flex items-center gap-1.5 mb-4"><ArrowLeft size={15} /> Back</button>
      <div className="rounded-2xl border border-rose-100 bg-rose-50 p-8 text-center text-rose-700">Could not load this service contract.</div>
    </div>
  );

  const c = data.contract || {};
  const k = data.kpis || {};
  const di = (v) => (v || '').slice(0, 10);
  const EI = 'input-field w-full text-sm';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="w-full px-6 py-3 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate(`/plan-management/participants/${c.client_id}`)} className="text-slate-500 hover:text-slate-800 flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> {c.client_name}</button>
          <ChevronRight size={14} className="text-slate-300" />
          {/* The reference titles this screen "<ref> for <participant>" — the contract number alone
              is not a name anybody recognises, and this page is reachable from several places. */}
          <span className="text-sm font-semibold text-slate-800"><span className="font-mono">{c.ref}</span> <span className="text-slate-400 font-normal">for</span> {c.client_name}</span>
          <span className={clsx('text-[11px] font-semibold px-2 py-0.5 rounded-full', (c.contract_status === 'inactive' || c.superseded_by) ? 'bg-slate-200 text-slate-600' : c.contract_status === 'new' ? 'bg-sky-50 text-sky-700' : 'bg-emerald-50 text-emerald-700')}>{cap(c.contract_status || 'Active')}</span>
          {c.pace_participant && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">NDIS-PACE</span>}
          <span className="flex-1" />
          {editing ? (
            <>
              <button onClick={() => { setEditing(false); setForm(c); }} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><X size={14} /> Cancel</button>
              <button onClick={save} disabled={busy} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>
            </>
          ) : (
            <div className="relative">
              <button onClick={() => setActionsOpen((v) => !v)} aria-expanded={actionsOpen}
                className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5">Actions <ChevronDown size={13} className={clsx('transition motion-reduce:transition-none', actionsOpen && 'rotate-180')} /></button>
              {actionsOpen && (
                <>
                  <button type="button" aria-label="Close menu" className="fixed inset-0 z-10 cursor-default" onClick={() => setActionsOpen(false)} />
                  <div className="absolute right-0 mt-1 z-20 w-56 bg-white border border-slate-200 rounded-xl shadow-lg py-1">
                    <button onClick={() => { setForm(c); setEditing(true); setActionsOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><Pencil size={13} className="text-slate-400" /> Edit contract details</button>
                    {/* Both land on the section they name. The profile's forecast banner and its
                        Invoices tab are addressable, so neither of these drops the operator on a
                        default screen and leaves them to find the thing the menu promised. */}
                    <button onClick={() => { setActionsOpen(false); navigate(`/plan-management/participants/${c.client_id}`); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><TrendingUp size={13} className="text-slate-400" /> Budget forecast</button>
                    <button onClick={() => { setActionsOpen(false); navigate(`/plan-management/participants/${c.client_id}?tab=invoices`); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><FileText size={13} className="text-slate-400" /> Invoices for this participant</button>
                    <button onClick={() => { setActionsOpen(false); navigate('/plan-management?view=statements'); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><Receipt size={13} className="text-slate-400" /> Monthly statements</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-6 py-6 space-y-4">
        {/* KPI strip — the same six figures as the participant profile, so a number never changes
            meaning between the two screens. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
          <KPI label="Total Funding" value={money(k.funding)} dot="bg-sky-500" hint="The plan's total NDIS budget." />
          <KPI label="Total Allocation" value={money(k.allocation)} dot="bg-blue-500" hint="How much of the budget is assigned to category & service budgets." />
          <KPI label="Total Remaining" value={money(k.remaining)} dot="bg-cyan-500" alert={k.remaining < 0} hint="Budget not yet spent (delivered)." />
          <KPI label="Total Delivered" value={money(k.delivered)} dot="bg-indigo-500" hint="Value of services delivered / invoiced so far." />
          <KPI label="Total Claimed" value={money(k.claimed)} dot="bg-violet-500" hint="Amounts claimed from the NDIA." />
          <KPI label="Total Unclaimed" value={money(k.unclaimed)} dot="bg-fuchsia-500" hint="Delivered but not yet claimed from the NDIA." />
        </div>

        {Number(k.delivered) === 0 && (
          <div className="flex items-center gap-2 text-sm text-slate-500 bg-white border border-slate-200 rounded-xl px-4 py-2.5">
            <FileText size={15} className="text-slate-400 shrink-0" /> No invoices recorded on this plan yet — provider invoices will draw down the budgets below.
          </div>
        )}

        {/* 1. Service Contract Details */}
        <Section icon={FileText} title="Service Contract Details">
          {!editing ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
              <D label="Service Contract" value={c.ref} />
              <D label="Status" value={cap(c.contract_status || 'Active')} />
              <D label="Contract Number" value={c.contract_number} />
              <D label="Funding Source" value={c.funding_source} />
              <D label="Funding Management" value={c.funding_management} />
              <D label="Total Budget" value={money(c.budget)} />
              <D label="Referral Date" value={c.referral_date ? fmtD(c.referral_date) : null} />
              <D label="Start" value={c.start_date ? fmtD(c.start_date) : null} />
              <D label="End" value={c.end_date ? fmtD(c.end_date) : null} />
              <D label="Set Up Completed By" value={c.setup_completed_by} />
              <D label="Assigned To" value={c.assigned_to} />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
              <div><label className="text-[11px] text-slate-500">Status</label><select className={EI} value={form.contract_status || 'active'} onChange={setF('contract_status')}><option value="new">New</option><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
              <div><label className="text-[11px] text-slate-500">Contract number</label><input className={EI} value={form.contract_number || ''} onChange={setF('contract_number')} /></div>
              <div><label className="text-[11px] text-slate-500">Contract ref</label><input className={EI} value={form.contract_ref || c.ref} onChange={setF('contract_ref')} /></div>
              <div><label className="text-[11px] text-slate-500">Funding source</label><select className={EI} value={form.funding_source || 'NDIS'} onChange={setF('funding_source')}><option>NDIS</option><option>Plan</option><option>Self</option></select></div>
              <div><label className="text-[11px] text-slate-500">Funding management</label><select className={EI} value={form.funding_management || ''} onChange={setF('funding_management')}><option value="">—</option><option>Registered Plan Management Provider</option><option>Self-Managed</option><option>Agency Managed</option></select></div>
              <div><label className="text-[11px] text-slate-500">Total budget</label><input type="number" step="0.01" className={EI} value={form.budget ?? ''} onChange={setF('budget')} /></div>
              <div><label className="text-[11px] text-slate-500">Referral date</label><input type="date" className={EI} value={di(form.referral_date)} onChange={setF('referral_date')} /></div>
              <div><label className="text-[11px] text-slate-500">Contract start</label><input type="date" className={EI} value={di(form.start_date)} onChange={setF('start_date')} /></div>
              <div><label className="text-[11px] text-slate-500">Contract end</label><input type="date" className={EI} value={di(form.end_date)} onChange={setF('end_date')} /></div>
              <div><label className="text-[11px] text-slate-500">Set up completed by</label><input className={EI} value={form.setup_completed_by || ''} onChange={setF('setup_completed_by')} /></div>
              <div><label className="text-[11px] text-slate-500">Assigned to</label><input className={EI} value={form.assigned_to || ''} onChange={setF('assigned_to')} /></div>
            </div>
          )}
        </Section>

        {/* 2. Plan / PACE Details */}
        <Section icon={Layers} title="Plan / PACE Details">
          {!editing ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
              <D label="NDIS Plan Start" value={c.ndis_plan_start ? fmtD(c.ndis_plan_start) : null} />
              <D label="NDIS Plan End" value={c.ndis_plan_end ? fmtD(c.ndis_plan_end) : null} />
              <D label="Review Submit Date" value={c.review_submit_date ? fmtD(c.review_submit_date) : null} />
              <D label="Progress Report Date" value={c.progress_report_date ? fmtD(c.progress_report_date) : null} />
              <D label="NDIS PACE Participant" value={c.pace_participant ? 'Yes' : 'No'} />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
              <div><label className="text-[11px] text-slate-500">NDIS plan start</label><input type="date" className={EI} value={di(form.ndis_plan_start)} onChange={setF('ndis_plan_start')} /></div>
              <div><label className="text-[11px] text-slate-500">NDIS plan end</label><input type="date" className={EI} value={di(form.ndis_plan_end)} onChange={setF('ndis_plan_end')} /></div>
              <div><label className="text-[11px] text-slate-500">Review submit date</label><input type="date" className={EI} value={di(form.review_submit_date)} onChange={setF('review_submit_date')} /></div>
              <div><label className="text-[11px] text-slate-500">Progress report date</label><input type="date" className={EI} value={di(form.progress_report_date)} onChange={setF('progress_report_date')} /></div>
              <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.pace_participant} onChange={setF('pace_participant')} /> NDIS PACE participant</label>
            </div>
          )}
        </Section>

        {/* 3–6 */}
        {budget === null ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>
          : budget === false ? <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">Could not load the plan budget.</div>
            : (
              <>
                <PlanBudgetSection planId={planId} data={budget} reload={reloadAll} />
                <FundingComponentSection planId={planId} />
                <StatedSupportsSection planId={planId} data={budget} reload={reloadAll} />
                <ProviderBudgetsSection planId={planId} />
              </>
            )}
      </div>
    </div>
  );
}
